import React, {useEffect, useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import RadioGroup from '@mui/material/RadioGroup';
import ToolbarHeader from '../../../common/ToolbarHeader';
import useScreenWidth from '../../../hooks/useScreenWidth';
import {makeStyles} from '@mui/styles';
import PrimaryButton from '../../../common/StyledComponents/PrimaryButton';
import {theme} from '../../../Theme';
import {AttachFile, DeleteOutlined, Warning} from '@mui/icons-material';
import CancelProjectDialog from './Tabs/DetailsTab/CancelProjectDialog';
import FixedFooter from '../../../common/FixedFooter';
import SecondaryButton from '../../../common/StyledComponents/SecondaryButton';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {reloadRfpsState} from '../../../recoil/reloadRfps/reloadRfpsAtom';
import {loadingState} from '../../../recoil/bidmiiCustomLoader/bidmiiCustomLoaderAtom';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {IRequestForProposalPrivate} from '@bidmii/common/lib/server/IServerApi';
import {API_URL} from '../../../constants';
import useLocationForm from '../../LocationForm/useLocationForm';
import {updatePrePackagedDetails} from '../../../util/UpdateTemplateDetails';
import * as Sentry from '@sentry/react';
import {userState} from '../../../recoil/user/userAtom';
import BidmiiTextField from '../../../common/StyledComponents/BidmiiTextField';
import {LazyCKEditor} from '../../../common/LazyLoader';
import useCategoryMultiSelect from '../../../hooks/useCategoryMultiSelect';
import AlertDialog from '../../../common/dialogs/alertDialog';
import BidmiiRadio from '../../../common/StyledComponents/BidmiiRadio';
import useTemplateWizard from '../../NewProjectWizard/TemplateProjects/useTemplateWizard';
import {useHistory} from 'react-router-dom';
import TertiaryButton from '../../../common/StyledComponents/TertiaryButton';
import DocumentsList from '../../../common/DocumentsList';
import {handleFiles} from '../../../util/FileUtils';
import paymentRejections from '../../../util/paymentRejections';
import useSnackbar from '../../../hooks/useSnackbar';

interface IProps {
  handleClose: any;
  requestForProposal: IRequestForProposalPrivate;
  updateData: () => void;
}

const useStyles = makeStyles((theme: $TSFixMe) => ({
  root: {
    flexGrow: 1,
    padding: window.innerWidth > 600 ? 24 : 10,
    paddingBottom: 80,
    overflowY: 'scroll',
    maxHeight: '100vh',
    minHeight: window.innerWidth > 600 ? '60vh' : '100vh',
  },
}));

export default function EditRequestForProposal(props: IProps) {
  const rfp = props.requestForProposal;
  const classes = useStyles();
  const history = useHistory();
  const width = useScreenWidth();
  const {openSnackbar} = useSnackbar();

  const user = useRecoilValue(userState);
  const setReloadRfps = useSetRecoilState(reloadRfpsState);
  const setLoading = useSetRecoilState(loadingState);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [unsavedChangesOpen, setUnsavedChangesOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [categoryIds, setCategoryIds] = useState(rfp.categoryIds?.join());
  const [rfpValues, setRfpValues] = useState<any>({});
  const [approvedForCalls, setApprovedForCalls] = useState<string | null>(
    props.requestForProposal.approvedForCalls != null
      ? props.requestForProposal.approvedForCalls.toString()
      : null,
  );

  useEffect(() => {
    setRfpValues({
      title: rfp.title,
      description: rfp.description,
      privacy: rfp.privacy,
    });
  }, [rfp]);

  const [getAddress, locationForm, setAddressParts] = useLocationForm({
    onlyText: true,
    setDirty,
  });

  useEffect(() => {
    setAddressParts({field: 'street', payload: rfp.street});
    setAddressParts({field: 'city', payload: rfp.city});
    setAddressParts({field: 'postal', payload: rfp.postal});
    setAddressParts({
      field: 'province',
      payload: rfp.province,
    });
  }, [setAddressParts, rfp]);

  const [categoryMultiSelect, selectedCategories] = useCategoryMultiSelect({
    categoryIds: rfp.categoryIds,
    noLabel: true,
  });

  const [templateInputs, details] = useTemplateWizard({
    project: rfp,
    templateKey: rfp.templateId,
    edit: true,
    setDirty,
  });

  useEffect(() => {
    if (
      selectedCategories?.length > 0 &&
      selectedCategories.length !== rfp.categoryIds?.length
    ) {
      setCategoryIds(selectedCategories.map((cat: $TSFixMe) => cat.id).join());
      setDirty(true);
    }
  }, [rfp.categoryIds?.length, selectedCategories]);

  const handleExitEditing = () => {
    if (dirty) {
      setUnsavedChangesOpen(true);
    } else {
      props.handleClose();
    }
  };

  const handleChangeRfpValues = (field: string, value: any) => {
    setDirty(true);
    setRfpValues({...rfpValues, [field]: value});
  };

  const submitChangedValues = () => {
    if (!dirty) {
      props.handleClose();
      return;
    }

    setLoading(true);
    if (details.length > 0) {
      updatePrePackagedDetails(details);
    }
    getAddress().then((address: any) => {
      return securedFetchRequest(
        `${API_URL}/rfps/${props.requestForProposal.uid}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...rfpValues,
            ...address,
            categoryIds,
          }),
        },
      )
        .then((res) => res.json())
        .then((res) => {
          setDirty(false);
          setLoading(false);
          props.updateData();
          props.handleClose();

          openSnackbar('Your project details have been updated.');
        })
        .catch((err) => {
          setLoading(false);
          process.env.NODE_ENV === 'development' && console.error(err);

          Sentry.withScope(function (scope) {
            scope.setUser({
              uid: user.uid,
              firstName: user.firstName,
            });
            scope.setTag('section', 'my_rfps_dialog');
            Sentry.captureException(err);
          });
        });
    });
  };

  const handleClickDeleteProject = () => {
    setCancelOpen(true);
  };

  const confirmCancelProject = (reason: $TSFixMe) => {
    setLoading(true);
    const url = `${API_URL}/rfps/${props.requestForProposal.uid}?reason=${reason}`;
    return securedFetchRequest(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.text())
      .then((res) => {
        setLoading(false);
        setCancelOpen(false);

        props.handleClose();
        openSnackbar('Your project has been cancelled');
        history.push('/myRfps');
        setReloadRfps(true);
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        Sentry.withScope(function (scope) {
          scope.setTag(
            'section',
            'EditRequestForProposal.tsx confirmCancelProject()',
          );
          Sentry.captureException(err);
        });
      });
  };

  const onChangeHandler = (event: $TSFixMe, editor: $TSFixMe) => {
    const data = editor.getData();
    setDirty(true);
    setRfpValues({...rfpValues, description: data});
  };

  const onImageCapture = (
    event: $TSFixMe,
    key: string,
    description: string,
  ) => {
    setLoading(true);
    const attachmentInfo = {
      key,
      description,
      resModel: 'RequestForProposal',
      resId: `${rfp.id}`,
    };
    handleFiles(event, () => props.updateData(), attachmentInfo);
  };

  const hasProposals = useMemo(
    () => props.requestForProposal.meta.proposalsCount > 0,
    [props.requestForProposal.meta.proposalsCount],
  );

  const submitCallApproval = () => {
    setLoading(true);
    const body = {
      approvedForCalls: approvedForCalls === 'true',
    };
    securedFetchRequest(`${API_URL}/rfps/${props.requestForProposal.uid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then((res) => {
        props.updateData();
        setLoading(false);
        openSnackbar('CALL SETTINGS UPDATED');
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        setLoading(false);
        openSnackbar(paymentRejections(err.message), 'error');

        Sentry.withScope(function (scope) {
          scope.setTag('section', 'bid_range_payment');
          Sentry.captureException(err);
        });
      });
  };

  useEffect(() => {
    if (
      approvedForCalls !== null &&
      approvedForCalls !==
        props.requestForProposal?.approvedForCalls?.toString()
    ) {
      submitCallApproval();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedForCalls]);

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      fullScreen={width <= 600}
      PaperProps={{style: {borderRadius: width > 600 ? 10 : 0}}}>
      <ToolbarHeader title="" handleClose={handleExitEditing} />
      <div className={classes.root}>
        {['DRAFT', 'OPEN'].includes(props.requestForProposal.state) && (
          <Grid container justifyContent="flex-end" style={{marginBottom: 24}}>
            <PrimaryButton
              color={theme.palette.red.main}
              textColor={theme.palette.secondary.main}
              size="small"
              onClick={handleClickDeleteProject}>
              <DeleteOutlined fontSize="small" style={{marginRight: 4}} />
              DELETE PROJECT
            </PrimaryButton>
          </Grid>
        )}

        {hasProposals ? (
          <Box
            my={5}
            p={1.5}
            style={{
              borderRadius: 6,
              backgroundColor: theme.palette.gray.light,
              border: `1px solid ${theme.palette.red.main}`,
            }}>
            <Grid container>
              <Warning
                style={{
                  fontSize: 20,
                  marginRight: 8,
                  color: theme.palette.red.main,
                }}
              />
              <Typography
                component="span"
                variant="body2"
                style={{fontSize: 13, flex: 1}}>
                The details and location of this project cannot be edited
                because you have one or more bids already on this project.
              </Typography>
            </Grid>
          </Box>
        ) : (
          <>
            <BidmiiTextField
              fullWidth
              gutterBottom
              label="Project title"
              defaultValue={rfp.title}
              onChange={(e) => handleChangeRfpValues('title', e.target.value)}
            />

            <LazyCKEditor
              label="Project description"
              // @ts-ignore
              data={rfpValues.description}
              config={{
                toolbar: [],
              }}
              onChange={(event: $TSFixMe, editor: $TSFixMe) =>
                onChangeHandler(event, editor)
              }
            />

            {rfp.templateId && (
              <Box my={3}>
                <Divider style={{marginBottom: 16}} />
                <Typography variant="h6" gutterBottom>
                  Template Details
                </Typography>
                {templateInputs}
                <Divider />
              </Box>
            )}

            <Box my={5}>
              <Typography variant="h6" fontSize={15}>
                Categories
              </Typography>
              {categoryMultiSelect}
            </Box>

            <Box mb={4}>
              <Typography variant="h6" fontSize={15}>
                Project Location
              </Typography>
              {locationForm}
            </Box>

            <Box mb={5}>
              <Typography variant="h6" fontSize={15}>
                Visibility
              </Typography>
              <RadioGroup
                aria-label="visibility"
                name="privacy"
                value={rfpValues.privacy}
                onChange={(e) =>
                  handleChangeRfpValues('privacy', e.target.value)
                }
                style={{marginBottom: 20}}>
                <BidmiiRadio
                  state={rfpValues.privacy}
                  value="anyone"
                  label="Public"
                />
                <BidmiiRadio
                  state={rfpValues.privacy}
                  value="contractors"
                  label="Contractors only"
                />
              </RadioGroup>
            </Box>
          </>
        )}

        <Box mb={4}>
          <Typography variant={'h6'} fontSize={15} gutterBottom>
            Would you like to receive calls from contractors?
          </Typography>
          <Typography variant={'body1'} fontSize={13}>
            Don't worry. We'll keep your phone number hidden for privacy.
          </Typography>
          <RadioGroup
            aria-label="receive calls"
            name="calls_status"
            value={approvedForCalls}
            onChange={(e) => setApprovedForCalls(e.target.value)}>
            <BidmiiRadio
              state={approvedForCalls}
              value="true"
              label="Accept calls"
            />
            <BidmiiRadio
              state={approvedForCalls}
              value="false"
              label="No calls"
            />
          </RadioGroup>
        </Box>

        <Box mb={3}>
          <input
            style={{display: 'none'}}
            id="project-attachment"
            type="file"
            onChange={(e) =>
              onImageCapture(e, 'project_document', 'Project document')
            }
          />
          <label htmlFor="project-attachment">
            <Grid container alignItems="center">
              <AttachFile fontSize="small" />
              <TertiaryButton
                bold
                aria-label="Upload documents"
                component="span">
                Upload Document (optional)
              </TertiaryButton>
            </Grid>
          </label>
          <Box mt={1}>
            <DocumentsList
              attachmentReferences={rfp.attachmentReferences.filter(
                (ref: any) => ref.key === 'project_document',
              )}
              editable
              updateRequestForProposalState={props.updateData}
            />
          </Box>
        </Box>
      </div>

      <FixedFooter appbar={width <= 600}>
        <SecondaryButton onClick={handleExitEditing}>CANCEL</SecondaryButton>
        <PrimaryButton onClick={submitChangedValues} style={{marginLeft: 24}}>
          SAVE CHANGES
        </PrimaryButton>
      </FixedFooter>

      <CancelProjectDialog
        open={cancelOpen}
        handleClose={() => setCancelOpen(false)}
        onSubmit={confirmCancelProject}
      />

      <AlertDialog
        open={unsavedChangesOpen}
        title="You have unsaved changes."
        body="Do you want to save your changes before closing?"
        confirm="SAVE"
        close="DISCARD"
        onConfirm={submitChangedValues}
        onClose={props.handleClose}
        onCancel={() => setUnsavedChangesOpen(false)}
      />
    </Dialog>
  );
}
